import { useEffect, useRef, useState } from "react";
import AppLayout from "@/components/AppLayout";
import api, { API, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Upload, FileText, Image as ImageIcon, FileType, Trash2, Download } from "lucide-react";
import EmptyState from "@/components/EmptyState";
import { toast } from "sonner";

const CATEGORIES = ["insurance", "lease", "mortgage", "tax", "incident", "general"];

function fileIcon(ct) {
  if (!ct) return FileText;
  if (ct.startsWith("image/")) return ImageIcon;
  if (ct.includes("pdf")) return FileType;
  return FileText;
}

export default function DocumentsPage() {
  const [files, setFiles] = useState([]);
  const [properties, setProperties] = useState([]);
  const [category, setCategory] = useState("general");
  const [propertyId, setPropertyId] = useState("");
  const [uploading, setUploading] = useState(false);
  const [filterCat, setFilterCat] = useState("all");
  const inputRef = useRef();

  const load = async () => {
    const params = filterCat !== "all" ? { params: { category: filterCat } } : {};
    const [f, p] = await Promise.all([api.get("/files", params), api.get("/properties")]);
    setFiles(f.data); setProperties(p.data);
  };
  useEffect(() => { load(); }, [filterCat]);

  const onPick = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", f);
    fd.append("category", category);
    if (propertyId && propertyId !== "none") fd.append("property_id", propertyId);
    try {
      await api.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Uploaded");
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    setUploading(false);
    if (inputRef.current) inputRef.current.value = "";
  };

  const del = async (id) => {
    if (!window.confirm("Delete this file?")) return;
    await api.delete(`/files/${id}`); load();
  };

  const download = async (f) => {
    try {
      const res = await api.get(`/files/${f.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = f.original_filename;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch { toast.error("Download failed"); }
  };

  return (
    <AppLayout
      title="Documents & Insurance"
      subtitle="Upload, organise & retrieve paperwork"
    >
      <Card className="p-6 border-slate-200 mb-6" data-testid="upload-card">
        <h3 className="font-display font-semibold mb-4">Upload paperwork</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="space-y-1.5">
            <Label className="text-xs">Category</Label>
            <Select value={category} onValueChange={setCategory}>
              <SelectTrigger data-testid="upload-category"><SelectValue /></SelectTrigger>
              <SelectContent>{CATEGORIES.map(c => <SelectItem key={c} value={c} className="capitalize">{c}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Property (optional)</Label>
            <Select value={propertyId} onValueChange={setPropertyId}>
              <SelectTrigger data-testid="upload-property"><SelectValue placeholder="None" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">— None —</SelectItem>
                {properties.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs invisible">Action</Label>
            <label className="block">
              <input ref={inputRef} type="file" hidden onChange={onPick} data-testid="file-input" />
              <Button asChild disabled={uploading} className="w-full bg-slate-900 hover:bg-slate-800 gap-2 cursor-pointer">
                <span><Upload className="h-4 w-4" /> {uploading ? "Uploading…" : "Choose & Upload File"}</span>
              </Button>
            </label>
          </div>
        </div>
      </Card>

      <div className="flex items-center gap-3 mb-4">
        <Label className="text-xs">Filter:</Label>
        <Select value={filterCat} onValueChange={setFilterCat}>
          <SelectTrigger data-testid="filter-category" className="w-48 h-9"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All categories</SelectItem>
            {CATEGORIES.map(c => <SelectItem key={c} value={c} className="capitalize">{c}</SelectItem>)}
          </SelectContent>
        </Select>
        <span className="text-xs text-slate-500 ml-auto">{files.length} file{files.length !== 1 && "s"}</span>
      </div>

      {files.length === 0 ? (
        <EmptyState
          kind="documents"
          eyebrow="The Vault"
          title="No paperwork on file yet."
          body="Upload leases, insurance policies, mortgage statements, receipts and any document worth keeping safe. Everything stored in encrypted object storage."
        />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="files-grid">
          {files.map(f => {
            const Icon = fileIcon(f.content_type);
            return (
              <Card key={f.id} data-testid={`file-${f.id}`} className="p-4 border-slate-200 hover:shadow-md transition-shadow duration-200 flex items-center gap-3">
                <div className="h-10 w-10 rounded-md bg-slate-100 flex items-center justify-center shrink-0">
                  <Icon className="h-5 w-5 text-slate-700" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium truncate">{f.original_filename}</div>
                  <div className="text-xs text-slate-500 capitalize">{f.category} · {(f.size/1024).toFixed(0)}KB</div>
                </div>
                <button onClick={() => download(f)} className="text-slate-400 hover:text-slate-900 transition-colors" data-testid={`download-${f.id}`}><Download className="h-4 w-4" /></button>
                <button onClick={() => del(f.id)} className="text-slate-400 hover:text-rose-600 transition-colors" data-testid={`delete-file-${f.id}`}><Trash2 className="h-4 w-4" /></button>
              </Card>
            );
          })}
        </div>
      )}
    </AppLayout>
  );
}
